import {
    Component,
    ElementRef,
    forwardRef,
    Host,
    OnChanges,
    Optional,
    SimpleChanges,
    SkipSelf,
    ViewChild
} from '@angular/core';

import {ControlContainer, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';

import {BehaviorSubject} from 'rxjs';

import {BaseComponent, AutocompleteControl} from '../../classes';

import {AutocompleteGroupInterface} from './interfaces/autocomplete-group.interface';
import {AutocompleteOptionInterface} from './interfaces/autocomplete-option.interface';

type AutocompleteValueType = string | number | string[] | number[];

@Component({
    selector: 'app-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => AutocompleteComponent),
        multi: true
    }]
})
export class AutocompleteComponent extends BaseComponent<AutocompleteValueType, AutocompleteValueType, AutocompleteControl>
    implements OnChanges {

    // CRM-5328: not `number`. The app build has no ambient node types, but the spec build does, and
    // there setTimeout is typed as returning NodeJS.Timeout - which failed to compile any spec that
    // reached this file, and so ruled out unit testing the forms that use the autocomplete controls.
    private timer: ReturnType<typeof setTimeout>;
    public autocompleteOptions: BehaviorSubject<AutocompleteOptionInterface[]>;
    public autocompleteGroups: BehaviorSubject<AutocompleteGroupInterface[]>;

    @ViewChild('input', {static: true}) public inputReference: ElementRef;
    @ViewChild(MatAutocompleteTrigger, {static: true}) autocompleteTrigger: MatAutocompleteTrigger;

    constructor(
        @Optional() @Host() @SkipSelf()
        protected controlContainer: ControlContainer
    ) {
        super(controlContainer);
    }

    private getOptions(): AutocompleteOptionInterface[] {
        if (!this.asFormsControl().grouped) {
            return this.asFormsControl().options;
        }

        const groupsOptions = this.asFormsControl().groups
            .map((group: AutocompleteGroupInterface) => group.options.items);

        return [].concat(...groupsOptions);
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.asFormsControl();

        if (typeof this.asFormsControl().checkForEmptyInputReference !== 'undefined') {
            this.asFormsControl().checkForEmptyInputReference = this.checkForEmptyInputReference.bind(this);
        }

        if (!this.asFormsControl().grouped) {
            this.autocompleteOptions = new BehaviorSubject(this.asFormsControl().options);
        } else {
            this.autocompleteGroups = new BehaviorSubject(this.asFormsControl().groups);
        }

        this.checkInvalid();
    }

    filterOptions(event): void {
        const value = event.target.value;

        this.executeFilter(value);
    }

    filterOptionsOnPaste(event: ClipboardEvent): void {
        const value = event.clipboardData.getData('text');

        this.executeFilter(value);
    }

    executeFilter(value: string): void {
        const pattern = value.toLowerCase()
            .replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

        const regex = new RegExp('.*' + pattern + '.*');

        let options = [];

        if (this.asFormsControl().async) {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.asFormsControl().asyncOptions(value).subscribe(response => {
                    this.autocompleteOptions.next(response);
                });
            }, 500);
        }
        else if (!this.asFormsControl().grouped) {
            options = this.asFormsControl().options.filter((option: AutocompleteOptionInterface) => {
                return regex.test(option.label.toLowerCase());
            });

            this.autocompleteOptions.next(options);
        } else {
            const controlValues = this.asFormsControl().groups.filter((group: AutocompleteGroupInterface) => {
                const optionMatches = group.options.items.filter((option: AutocompleteOptionInterface) => {
                    options.push(option);

                    return regex.test(option.label.toLowerCase());
                });

                return regex.test(group.options.name.toLowerCase()) || (optionMatches.length !== 0);
            });

            this.autocompleteGroups.next(controlValues);
        }

        this.control.resetErrors();

        if (options.length !== 1) {
            this.asFormsControl().resetValue();
            this.changeValue(null);
        } else {
            if (options[0].label !== value) {
                this.asFormsControl().resetValue();
                this.changeValue(null);
            } else {
                this.changeValue(options[0].value);
                this.autocompleteTrigger.closePanel();
            }
        }

        setTimeout(() => this.updateAutocompletePosition(), 1);
    }

    updateAutocompletePosition(): void {
        this.autocompleteTrigger.updatePosition();
    }

    checkForEmptyInputReference(): boolean {
        return this.inputReference.nativeElement.value === '';
    }

    selectOption(event: MatAutocompleteSelectedEvent) {
        this.inputReference.nativeElement.value = event.option.viewValue;

        this.changeValue(event.option.value);
    }

    changeValue(value: AutocompleteValueType): void {
        this.onChange(value);

        this.checkInvalid();
        this.control.resetErrors();

        super.writeValue(value);
    }

    writeValue(value: AutocompleteValueType): void {
        const filteredOptions = this.getOptions().filter((option: AutocompleteOptionInterface) => {
            return value === option.value;
        });

        if (filteredOptions.length !== 0) {
            this.inputReference.nativeElement.value = filteredOptions[0].label;
            super.writeValue(filteredOptions[0].value);
        } else {
            this.inputReference.nativeElement.value = '';

            if (typeof value !== 'string') {
                super.writeValue(null);
            } else {
                super.writeValue('');
            }
        }

        this.checkInvalid();
        this.control.resetErrors();
    }

    asFormsControl(): AutocompleteControl {
        if (!this.control || !(this.control instanceof AutocompleteControl)) {
            this.control = new AutocompleteControl({
                name: '',
                value: '',
                placeholder: '',
                validators: [],
                componentType: 0,
                invalid: false,
                errors: [],
                options: [],
                grouped: false,
                groups: [],
                checkForEmptyInputReference: null
            });
        }

        return (this.control as AutocompleteControl);
    }

    trackById(index: number, item: AutocompleteOptionInterface): AutocompleteValueType {
        return item.value;
    }
}
